import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { io } from 'socket.io-client';
import { Send, Plus, Download, FileText, Calendar, Clock, ChevronUp, ChevronDown, Wand2, Image, Camera, Sparkles, X, Pencil, Play, Maximize2 } from 'lucide-react';
import { auth } from '../../config/firebaseConfig';
import SuggestionModal from './SuggestionModal';
import CascadeProposedBanner from '../engine/CascadeProposedBanner';
import ImageViewer from './components/ImageViewer';
import VideoViewer from './components/VideoViewer';
import { getCachedMessages, setCachedMessages, appendCachedMessage, deleteCachedMessage, prependCachedMessages } from '../../utils/messageCache';
import { engineService } from '../../services/engineService';
import useGPSLocation from '../../hooks/useGPSLocation';

import { API_BASE_URL } from '../../config/apiConfig';

// ── AI 변환 캐시 helpers (localStorage) ───────────────────────────────────────
// 캐시 구조: { [msgId]: { king?: string, spellcheck?: string, spacing?: string } }
const TC_KEY = (rid) => `msgTransforms_${rid}`;
const loadTransformCache = (rid) => {
  try { const r = localStorage.getItem(TC_KEY(rid)); return r ? JSON.parse(r) : {}; }
  catch { return {}; }
};
const saveTransformCache = (rid, cache) => {
  try {
    const entries = Object.entries(cache);
    const trimmed = entries.length > 300 ? Object.fromEntries(entries.slice(-300)) : cache;
    localStorage.setItem(TC_KEY(rid), JSON.stringify(trimmed));
  } catch {}
};
// ─────────────────────────────────────────────────────────────────────────────

const GroupChat = ({ roomId, user, isMobile, aiModes = {}, searchQuery = '', hideScrollBtn = false, localNicknames = {}, roomMembers = [], onAddNotice = null, onMessageSent = null, onEngineCandidatesUpdated = null, onEngineCandidatesUpdate = null, onEngineStateChange = null, onAresSuggestionClick = null, onSuggestionUpdated = null }) => {
  // 닉네임 우선순위: 내가 설정한 로컬 > 상대방이 설정한 서버 닉네임 > 기본 이름
  const getDisplayName = (senderId, defaultName) => {
    const id = senderId?.toString();
    if (localNicknames[id]) return localNicknames[id];
    const member = roomMembers.find(m => (m.user?._id || m.user)?.toString() === id);
    if (member?.nickname) return member.nickname;
    return defaultName || '?';
  };

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isUploading, setIsUploading] = useState(false); // 파일 업로드 중
  const [isSending, setIsSending] = useState(false); // 메시지 전송 중
  const [isSocketConnected, setIsSocketConnected] = useState(true); // 소켓 연결 상태
  const [toast, setToast] = useState(null); // 토스트 알림 { message, type }
  const [cascadeProposal, setCascadeProposal] = useState(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false); // 사용자가 스크롤 중인지
  const [showSuggestionModal, setShowSuggestionModal] = useState(false); // 일정관리 모달 표시
  const [isCorrecting, setIsCorrecting] = useState(false); // AI 오타 교정 중
  const [transformCache, setTransformCache] = useState(() => loadTransformCache(roomId)); // { msgId: { king?, spellcheck?, spacing? } }
  const [deleteTarget, setDeleteTarget] = useState(null); // 삭제 대상 메시지
  const [msgContextMenu, setMsgContextMenu] = useState(null); // { msg, x, y }
  const [editingMsg, setEditingMsg] = useState(null); // 수정 중인 메시지 { _id, content }
  const [showAttachMenu, setShowAttachMenu] = useState(false); // + 버튼 메뉴
  // AI 입력 변환 상태
  const [aiInputTransformed, setAiInputTransformed] = useState(false); // 변환 완료 → 전송버튼
  const [aiSuggestions, setAiSuggestions] = useState([]); // 애매한 경우 선택지
  const [isAiTransforming, setIsAiTransforming] = useState(false); // AI 변환 중 로딩
  const longPressTimer = useRef(null);
  const [viewerState, setViewerState] = useState(null); // { images, initialIndex }
  const [videoViewerState, setVideoViewerState] = useState(null); // { msg }
  const [inlinePlayingId, setInlinePlayingId] = useState(null); // 인라인 재생 중인 메시지 ID
  const [pendingFiles, setPendingFiles] = useState([]); // 업로드 전 미리보기: [{ file, url, isVideo }]
  const [conflictModal, setConflictModal] = useState(null); // 🆕 충돌 확인 모달
  const [typingUsers, setTypingUsers] = useState([]);        // 타이핑 중인 유저 목록
  const [readStatus, setReadStatus] = useState({ readMap: {}, totalCount: 0 }); // 읽음 상태
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [matchedIds, setMatchedIds] = useState([]);
  const [currentMatchIdx, setCurrentMatchIdx] = useState(-1);
  const prevSearchQueryRef = useRef('');
  const aiModesRef = useRef(aiModes);
  const messagesRef = useRef([]);
  const transformCacheRef = useRef(loadTransformCache(roomId));

  // 🆕 디버그: conflictModal 상태 변경 추적
  useEffect(() => {
  }, [conflictModal]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const socketRef = useRef(null);
  const onEngineCandidatesUpdatedRef = useRef(onEngineCandidatesUpdated);
  const onEngineCandidatesUpdateRef = useRef(onEngineCandidatesUpdate);
  const onSuggestionUpdatedRef = useRef(onSuggestionUpdated);
  const textareaRef = useRef(null);
  const inputContainerRef = useRef(null);
  const [inputAreaHeight, setInputAreaHeight] = useState(56);
  const readTimerRef = useRef(null); // 🆕 읽음 처리 디바운스 타이머
  const typingTimeoutRef = useRef({});   // { userId: timeoutId } — 타이핑 자동 해제
  const typingSentRef = useRef(false);   // 타이핑 emit 중복 방지
  const stopTypingTimerRef = useRef(null); // 입력 멈춤 감지 타이머
  const isLoadingMoreRef = useRef(false);
  const justLoadedMoreRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const aiTransformingRef = useRef(false); // 동기 가드 — 더블클릭 방지
  const kingEnabledAtRef = useRef(null);   // 왕모드 켠 시점 timestamp (ms) — 이후 메시지만 변환
  const prevKingRef = useRef(false);       // 왕모드 이전 상태 (켬→끔 전환 감지)
  const myEntryReadAtSetRef = useRef(false);    // myEntryReadAt 중복 설정 방지
  const [myEntryReadAt, setMyEntryReadAt] = useState(undefined); // 입장 시점 lastReadAt (state → 리렌더 보장)
  const unreadDividerRef = useRef(null);        // "여기까지 읽었습니다" 구분선 DOM ref
  const hasScrolledInitiallyRef = useRef(false); // 초기 구분선 스크롤 완료 여부

  // GPS 위치 수집 (채팅방 입장 시 1회 + 5분마다 갱신)
  useGPSLocation();

  // ref 동기화
  useEffect(() => { aiModesRef.current = aiModes; }, [aiModes]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // 입력창 높이 변화 감지 → 스크롤 버튼 위치 동적 조정
  useEffect(() => {
    const el = inputContainerRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => setInputAreaHeight(el.offsetHeight));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => { transformCacheRef.current = transformCache; }, [transformCache]);
  useEffect(() => { onEngineCandidatesUpdatedRef.current = onEngineCandidatesUpdated; }, [onEngineCandidatesUpdated]);
  useEffect(() => { onEngineCandidatesUpdateRef.current = onEngineCandidatesUpdate; }, [onEngineCandidatesUpdate]);
  useEffect(() => { onSuggestionUpdatedRef.current = onSuggestionUpdated; }, [onSuggestionUpdated]);

  // textarea 자동 높이 조절
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, 120);
    el.style.height = newHeight + 'px';
    el.style.overflowY = el.scrollHeight > 120 ? 'auto' : 'hidden';
  }, [input]);

  // aiModes가 바뀌면 (모드 껐을 때) AI 입력 상태 초기화
  useEffect(() => {
    const anyActive = aiModes?.spellcheck || aiModes?.spacing || aiModes?.king || aiModes?.smart;
    if (!anyActive) {
      setAiInputTransformed(false);
      setAiSuggestions([]);
    }
  }, [aiModes?.spellcheck, aiModes?.spacing, aiModes?.king]);

  // ── AI 변환 헬퍼: 왕모드 전용 (king/subject만 로컬 표시)
  // spellcheck/spacing/smart는 전송 전 입력창에서 처리 → 서버 저장 → 모두에게 보임
  const applyTransforms = async (msg, activeModes, token) => {
    const currentCache = transformCacheRef.current;
    const patch = {};
    const memberNames = roomMembers
      .map(m => {
        const u = m.user || m;
        return [u.firstName, u.lastName, u.nickname, m.nickname].filter(Boolean).join('');
      })
      .filter(n => n.length > 0);
    for (const mode of activeModes) {
      if (currentCache[msg._id]?.[mode]) continue; // 이미 캐시됨
      if (mode !== 'king' && mode !== 'subject') continue; // 왕모드만 로컬 변환
      try {
        const res = await fetch(`${API_BASE_URL}/api/chat/transform`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ text: msg.content, mode, memberNames })
        });
        const data = await res.json();
        if (data.transformed) patch[mode] = data.transformed;
      } catch {}
    }
    if (Object.keys(patch).length > 0) {
      setTransformCache(prev => {
        const next = { ...prev, [msg._id]: { ...(prev[msg._id] || {}), ...patch } };
        saveTransformCache(roomId, next);
        return next;
      });
    }
  };

  // ── 왕모드 켬/끔 시점 기록 ──────────────────────────────────────────────────
  // 켠 시점 이후 메시지만 변환하기 위해 timestamp 저장
  useEffect(() => {
    const isKing = !!aiModes?.king;
    if (isKing && !prevKingRef.current) {
      // OFF → ON: 현재 시각 기록
      kingEnabledAtRef.current = Date.now();
    } else if (!isKing) {
      // ON → OFF: 초기화
      kingEnabledAtRef.current = null;
    }
    prevKingRef.current = isKing;
  }, [aiModes?.king]);

  // ── 왕모드 ON 시: 미변환 메시지 일괄 변환 ────────────────────────────────────
  // 켠 시점(kingEnabledAtRef) 이후 메시지만 변환 → 토큰 절약
  // messages.length dep: 메시지 수 변화(초기로드/페이지네이션)에만 반응
  //   → 개별 메시지 도착(소켓)은 소켓 핸들러에서 처리하므로 중복 없음
  useEffect(() => {
    if (!aiModes?.king || messages.length === 0) return;
    const enabledAt = kingEnabledAtRef.current;
    if (!enabledAt) return;

    const myId = String(user?._id || user?.id || '');
    const myEmail = user?.email || '';
    const cache = transformCacheRef.current;

    const isMine = (msg) => {
      const s = msg.sender;
      if (!s) return false;
      if (s.email && myEmail) return s.email === myEmail;
      const sid = String(s._id || s.id || (typeof s === 'string' ? s : ''));
      return sid === myId;
    };

    // 켠 시점 이후 메시지만 필터링
    const afterEnabled = (msg) => {
      const t = msg.createdAt ? new Date(msg.createdAt).getTime() : 0;
      return t >= enabledAt;
    };

    const otherTargets = messages.filter(msg =>
      msg.type === 'text' && msg.content && !isMine(msg) && !cache[msg._id]?.subject && afterEnabled(msg)
    );
    const myTargets = messages.filter(msg =>
      msg.type === 'text' && msg.content && isMine(msg) && !cache[msg._id]?.king && afterEnabled(msg)
    );

    if (otherTargets.length === 0 && myTargets.length === 0) return;

    (async () => {
      try {
        const { auth } = await import('../../config/firebaseConfig');
        await auth.authStateReady?.();
        const token = await auth.currentUser?.getIdToken();
        for (let i = 0; i < otherTargets.length; i += 10) {
          await Promise.allSettled(otherTargets.slice(i, i + 10).map(msg => applyTransforms(msg, ['subject'], token)));
        }
        for (let i = 0; i < myTargets.length; i += 10) {
          await Promise.allSettled(myTargets.slice(i, i + 10).map(msg => applyTransforms(msg, ['king'], token)));
        }
      } catch {}
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiModes?.king, messages.length]);

  // 1. 초기 로드 및 소켓 연결
  useEffect(() => {
    // fetchReadStatus를 먼저 await → 구 lastReadAt 캡처 보장
    // 이유: fetchReadStatus와 markAsRead를 동시에 실행하면 markAsRead가 먼저 완료되어
    //       fetchReadStatus가 이미 갱신된 lastReadAt(=NOW)을 읽는 레이스 컨디션 발생
    //       → myEntryReadAt = NOW → 모든 메시지가 "읽음" → 구분선 표시 안 됨
    const initLoad = async () => {
      // 0. 캐시 즉시 표시 (서버 응답 대기 없이)
      const cached = await getCachedMessages(roomId);
      if (cached.length > 0) {
        setMessages(cached);
        setHasMore(cached.length >= 50);
      }
      await fetchReadStatus(); // 구 lastReadAt 먼저 캡처
      fetchMessages();         // 서버에서 최신 데이터 동기화 (캐시 덮어쓰기)
      markMessagesAsRead().then(() => {
        window.dispatchEvent(new Event('unread-count-changed'));
      });
    };
    initLoad();

    // 백그라운드 전환 시 readTimer 즉시 취소
    // 메시지 도착 직후(1초 이내) 백그라운드로 전환하는 경우 타이머가 남아있어 읽음 처리되는 버그 방지
    const handleVisibilityForRead = () => {
      if (document.hidden && readTimerRef.current) {
        clearTimeout(readTimerRef.current);
        readTimerRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityForRead);

    // 소켓 연결 (Firebase ID Token을 handshake auth에 포함해 서버 검증)
    // useEffect 콜백은 async 불가 → async IIFE로 감싸서 await 사용
    let socketSetupCancelled = false;
    (async () => {
      const firebaseToken = await auth.currentUser?.getIdToken().catch(() => null);
      if (socketSetupCancelled) return;
      socketRef.current = io(API_BASE_URL, {
        transports: ['websocket', 'polling'],
        auth: { token: firebaseToken },
      });

    // 연결 완료 후 room join
    const userId = user?._id || user?.id;
    socketRef.current.on('connect', () => {
      setIsSocketConnected(true);
      socketRef.current.emit('join-room', roomId);
      // FCM 푸시 제외 로직을 위해 userId도 같은 소켓에 등록
      if (userId) socketRef.current.emit('join-user', userId);
    });
    // 이미 연결된 상태라면 바로 join (재마운트 시 connect 이벤트 안 올 수 있음)
    if (socketRef.current.connected) {
      socketRef.current.emit('join-room', roomId);
      if (userId) socketRef.current.emit('join-user', userId);
    }

    // 소켓 끊김 감지
    socketRef.current.on('disconnect', () => {
      setIsSocketConnected(false);
    });

    // 소켓 재연결 시 누락 메시지 복구
    socketRef.current.on('reconnect', () => {
      setIsSocketConnected(true);
      fetchMessages();
    });

    // 연결 오류
    socketRef.current.on('connect_error', () => {
      setIsSocketConnected(false);
    });

    // 메시지 수신
    socketRef.current.on('chat-message', async (newMessage) => {
      setMessages((prev) => [...prev, newMessage]);
      appendCachedMessage(roomId, newMessage);
      // AI 변환: 상대방 메시지에 활성 모드 적용
      const myUserId = String(user?._id || user?.id || '');
      const myUserEmail = user?.email || '';
      if (newMessage.content && newMessage.type === 'text') {
        const s = newMessage.sender;
        const isOwnMsg = s
          ? (s.email && myUserEmail ? s.email === myUserEmail : String(s._id || s.id || (typeof s === 'string' ? s : '')) === myUserId)
          : false;
        // 왕모드만 로컬 표시 변환 (spellcheck/spacing/smart는 전송 전 처리됨)
        const modes = aiModesRef.current?.king
          ? [isOwnMsg ? 'king' : 'subject']
          : [];
        if (modes.length > 0) {
          try {
            const token = await (await import('../../config/firebaseConfig')).auth.currentUser?.getIdToken();
            await applyTransforms(newMessage, modes, token);
          } catch {}
        }
      }
      scrollToBottom();
      // 백그라운드 상태에서 받은 메시지는 읽음 처리 안 함
      // Android: JS가 백그라운드에서도 실행되어 소켓 메시지가 수신됨
      // → document.hidden일 때 markAsRead 하면 push 알림이 왔는데도 읽음 처리되는 버그
      if (document.hidden) return;
      // 읽음 처리 후 뱃지 갱신 (즉시 갱신하면 읽음 처리 전이라 badge가 쌓임)
      if (readTimerRef.current) clearTimeout(readTimerRef.current);
      readTimerRef.current = setTimeout(async () => {
        await markMessagesAsRead();
        window.dispatchEvent(new Event('unread-count-changed'));
      }, 1000);
    });

    // 메시지 삭제 수신
    socketRef.current.on('message-deleted', ({ messageId }) => {
      setMessages((prev) => prev.filter(msg => msg._id !== messageId));
      deleteCachedMessage(roomId, messageId);
    });

    // 메시지 수정 수신
    socketRef.current.on('message-edited', ({ messageId, content, isEdited, editedAt }) => {
      setMessages((prev) => prev.map(msg =>
        msg._id === messageId ? { ...msg, content, isEdited, editedAt } : msg
      ));
    });

    // 🆕 충돌 확인 필요 이벤트
    socketRef.current.on('conflict-confirmation-needed', (data) => {
      const currentUserId = user?._id || user?.id;
      if (currentUserId && data.targetUserId === currentUserId) {
        setConflictModal(data);
      }
    });

    // 타이핑 인디케이터 수신
    socketRef.current.on('typing', ({ userId, userName }) => {
      setTypingUsers((prev) => {
        if (prev.some((u) => u.userId === userId)) return prev;
        return [...prev, { userId, userName }];
      });
      // 3초 후 자동 제거 (stop-typing 못 받은 경우 대비)
      if (typingTimeoutRef.current[userId]) clearTimeout(typingTimeoutRef.current[userId]);
      typingTimeoutRef.current[userId] = setTimeout(() => {
        setTypingUsers((prev) => prev.filter((u) => u.userId !== userId));
      }, 3000);
    });

    socketRef.current.on('stop-typing', ({ userId }) => {
      if (typingTimeoutRef.current[userId]) clearTimeout(typingTimeoutRef.current[userId]);
      setTypingUsers((prev) => prev.filter((u) => u.userId !== userId));
    });

    // ARES 엔진 후보 업데이트 수신 (ref 사용 → 최신 함수 참조 보장)
    socketRef.current.on('engine:candidates-updated', (data) => {
      onEngineCandidatesUpdatedRef.current?.(data);
      onEngineCandidatesUpdateRef.current?.(data);
    });

    // 일정 제안 상태 변경 (수락/거절/불참 등) → 부모에 알림
    // 서버가 'suggestion-updated' (하이픈)으로 emit
    socketRef.current.on('suggestion-updated', (data) => {
      onSuggestionUpdatedRef.current?.(data?.suggestionId, data);
    });
    // 하위 호환: 구버전 서버 대비
    socketRef.current.on('suggestion:updated', (data) => {
      onSuggestionUpdatedRef.current?.(data?.suggestionId, data);
    });

    // 멤버 거절/불참 → 부모에 알림
    socketRef.current.on('member-declined', (data) => {
      onSuggestionUpdatedRef.current?.(data?.suggestionId);
    });

    // 일정 제안 삭제 → 부모에 알림
    socketRef.current.on('suggestion-deleted', (data) => {
      onSuggestionUpdatedRef.current?.(data?.suggestionId);
    });

    // 재참석/재구성으로 새 제안 Z 생성 → 부모에 'created'로 알림 (ARES 패널 + 일정관리 탭 실시간 갱신)
    socketRef.current.on('new-suggestion', (data) => {
      const sid = data?.suggestion?._id?.toString() || data?.suggestionId?.toString();
      if (sid) {
        onSuggestionUpdatedRef.current?.(sid, { status: 'created' });
      }
    });

    // 일정 확정/거절 → 전역 이벤트로 UI 갱신
    socketRef.current.on('schedule-confirmed-refresh', () => {
      window.dispatchEvent(new Event('scheduleConfirmedRefresh'));
    });
    socketRef.current.on('schedule-confirmed', (data) => {
      window.dispatchEvent(new CustomEvent('scheduleConfirmed', { detail: data }));
    });
    socketRef.current.on('schedule-rejected', () => {
      window.dispatchEvent(new Event('scheduleRejected'));
    });

    // 방 멤버 목록 변경 → 전역 이벤트로 부모 갱신 트리거
    socketRef.current.on('room-members-updated', (data) => {
      window.dispatchEvent(new CustomEvent('roomMembersUpdated', { detail: data }));
    });

    socketRef.current.on('engine:state-changed', (data) => {
      if (onEngineStateChange) onEngineStateChange(data);
    });

    // DG Engine 피로도/프로파일 갱신 알림 → useDGProfile 즉시 갱신
    socketRef.current.on('dg:profile-updated', () => {
      window.dispatchEvent(new Event('dgProfileUpdate'));
    });

    // DG 점수 업데이트 → 후보 패널 즉시 갱신 트리거
    socketRef.current.on('dg:scores-updated', (data) => {
      window.dispatchEvent(new CustomEvent('dgScoresUpdated', { detail: data }));
      // 노쇼 위험 토스트 (9번) — no_show_risk > 0.3이면 경고
      const highRisk = (data.scoredCandidates || []).find(
        sc => (sc.predictions?.no_show_risk || 0) > 0.3
      );
      if (highRisk) {
        setToast({ message: '⚠️ 이 참여자의 불참 위험이 높습니다', type: 'warning' });
      }
    });

    // 대체 후보가 있으면 사용자가 비교할 수 있는 배너로 유지합니다.
    socketRef.current.on('dg:cascade-proposed', (data) => {
      const alternatives = data.cascadeResult?.alternatives;
      if (Array.isArray(alternatives) && alternatives.length > 0) {
        setCascadeProposal(data);
        return;
      }

      const trigger = data.triggerAction;
      let msg;
      if (trigger === 'PROPOSE_TIME') {
        const who = data.proposerName ? `${data.proposerName}님이 ` : '';
        const when = data.proposedLabel ? `${data.proposedLabel}을 ` : '';
        msg = `${who}${when}제안했어요 — ARES 패널에서 후보를 확인해보세요`;
      } else if (trigger === 'OPPOSE_TIME') {
        msg = '다른 시간대 후보로 업데이트했어요';
      } else if (trigger === 'OPPOSE_PLACE') {
        msg = '다른 장소 후보로 업데이트했어요';
      } else if (trigger === 'CANCEL') {
        msg = '일정이 취소되어 대안을 검토했습니다';
      } else {
        const changes = data.cascadeResult?.changes;
        const count = Array.isArray(changes) ? changes.length : 0;
        msg = count > 0
          ? `일정 변경 감지 — ${count}개 대체 후보가 제안되었습니다`
          : '일정 변경으로 재배치가 검토되었습니다';
      }
      setToast({ message: msg, type: 'info' });
    });

    // 자동 확정 알림 토스트
    socketRef.current.on('schedule:auto-confirmed', ({ roomName }) => {
      setToast({ message: `✅ ${roomName || '일정'}이 자동 확정되었습니다`, type: 'success' });
      window.dispatchEvent(new CustomEvent('refreshUser'));
    });

    // 읽음 상태 실시간 업데이트
    socketRef.current.on('read-update', ({ userId, readAt }) => {
      setReadStatus((prev) => ({
        ...prev,
        readMap: { ...prev.readMap, [userId]: readAt },
      }));
    });
    })(); // async IIFE 종료

    return () => {
      socketSetupCancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityForRead);
      // 방 나갈 때 읽음 처리 + 타이핑 정리
      if (readTimerRef.current) clearTimeout(readTimerRef.current);
      if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
      Object.values(typingTimeoutRef.current).forEach((t) => clearTimeout(t));
      if (typingSentRef.current && socketRef.current?.connected) {
        socketRef.current.emit('stop-typing', { roomId, userId: user?._id || user?.id });
      }
      markMessagesAsRead();
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [roomId]);

  // 외부 이벤트(일정 생성/삭제 등)로 채팅 메시지 갱신
  useEffect(() => {
    const handleSuggestionUpdate = () => {
      fetchMessages();
    };
    window.addEventListener('suggestionUpdate', handleSuggestionUpdate);
    window.addEventListener('calendarUpdate', handleSuggestionUpdate);
    return () => {
      window.removeEventListener('suggestionUpdate', handleSuggestionUpdate);
      window.removeEventListener('calendarUpdate', handleSuggestionUpdate);
    };
  }, []);

  // 🆕 강제 참석 (충돌 무시)
  const handleForceAccept = async (suggestionId) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/suggestions/${suggestionId}/force-accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setConflictModal(null);
        setToast({ message: '일정에 참석했습니다', type: 'success' });
      } else {
        const error = await res.json();
        setToast({ message: error.message || '참석 처리에 실패했습니다.', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to force accept suggestion:', error);
      setToast({ message: '참석 처리 중 오류가 발생했습니다.', type: 'error' });
    }
  };

  const fetchMessages = async () => {
    try {
      // 서버에서 최신 데이터 가져와 동기화 (캐시 표시는 initLoad에서 처리)
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setMessages(data);
      setHasMore(data.length >= 50);
      // 캐시 업데이트
      await setCachedMessages(roomId, data);
      // 백그라운드: 이전 페이지 프리패치 → 스크롤 업 시 캐시에서 즉시 표시
      if (data.length >= 50) {
        const oldest = data[0];
        (async () => {
          try {
            const t = await auth.currentUser?.getIdToken();
            const r = await fetch(
              `${API_BASE_URL}/api/chat/${roomId}?limit=50&before=${encodeURIComponent(oldest.createdAt)}`,
              { headers: { Authorization: `Bearer ${t}` } }
            );
            const older = await r.json();
            if (older.length > 0) await prependCachedMessages(roomId, older);
          } catch { /* 무시 */ }
        })();
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const loadMoreMessages = async () => {
    if (isLoadingMoreRef.current || !hasMore || messages.length === 0) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);
    const oldest = messages[0];
    const container = messagesContainerRef.current;
    prevScrollHeightRef.current = container ? container.scrollHeight : 0;
    try {
      // 1. 캐시에서 이전 메시지 확인
      const allCached = await getCachedMessages(roomId);
      const olderInCache = allCached.filter(m => new Date(m.createdAt) < new Date(oldest.createdAt));
      if (olderInCache.length > 0) {
        const page = olderInCache.slice(-50);
        justLoadedMoreRef.current = true;
        setMessages(prev => [...page, ...prev]);
        // 캐시에 더 있거나, 캐시가 소진됐어도 서버에 더 있을 수 있으므로 true 유지
        setHasMore(true);
        return;
      }
      // 2. 캐시 없으면 서버 fetch
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(
        `${API_BASE_URL}/api/chat/${roomId}?limit=50&before=${encodeURIComponent(oldest.createdAt)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.length === 0) { setHasMore(false); return; }
      justLoadedMoreRef.current = true;
      setMessages(prev => [...data, ...prev]);
      setHasMore(data.length >= 50);
      // 서버에서 가져온 이전 메시지도 캐시에 저장
      prependCachedMessages(roomId, data);
    } catch (error) {
      console.error('Failed to load more messages:', error);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  };

  const markMessagesAsRead = async () => {
    try {
      // auth.currentUser가 null이면 Firebase 복원을 최대 3초 대기
      // 앱 재개(킬 후 알림 탭) 직후 마운트될 때 auth가 아직 복원 중일 수 있음
      // → null인 채로 token=undefined → 401 → ownerLastReadAt 미업데이트 → push unreadCount 누적 버그
      let currentUser = auth.currentUser;
      if (!currentUser) {
        currentUser = await new Promise((resolve) => {
          const timer = setTimeout(() => { unsub(); resolve(null); }, 3000);
          const unsub = auth.onAuthStateChanged((u) => { clearTimeout(timer); unsub(); resolve(u); });
        });
      }
      if (!currentUser) return;
      const token = await currentUser.getIdToken();
      await fetch(`${API_BASE_URL}/api/chat/${roomId}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      // Android: 알림 트레이에 남아있는 push 알림 닫기
      // iOS는 setAppBadge()로 처리되지만 Android는 직접 notification.close() 해야 배지 사라짐
      navigator.serviceWorker?.controller?.postMessage({ type: 'ROOM_READ', roomId });
      // SW controller가 null(SW 죽음)이어도 Cache를 직접 삭제
      // → 다음 push 수신 시 storedBadge 스테일 방지 (배지 누적 버그 방지)
      if ('caches' in window) {
        caches.open('sw-state-v1').then(c => c.delete('/sw-badge-count')).catch(() => {});
      }
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  };

  // 방 멤버 읽음 상태 초기 로드
  const fetchReadStatus = async () => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/read-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setReadStatus(data);
      // 입장 시점의 내 lastReadAt을 state로 저장 (최초 1회만)
      // state 사용 이유: ref는 값 변경 시 리렌더 안 됨 → 구분선이 렌더링 안 되는 버그
      // myId를 toString()으로 강제 변환: user._id가 ObjectId 객체일 때 키 매칭 실패 방지
      if (!myEntryReadAtSetRef.current) {
        myEntryReadAtSetRef.current = true;
        const myId = (user?._id || user?.id || '').toString();
        setMyEntryReadAt(data.readMap?.[myId] || null);
      }
    } catch (e) {
      if (!myEntryReadAtSetRef.current) {
        myEntryReadAtSetRef.current = true;
        setMyEntryReadAt(null);
      }
    }
  };

  // 메시지 ID로 스크롤
  const scrollToMessage = (msgId) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const goToPrev = () => {
    if (matchedIds.length === 0) return;
    const next = currentMatchIdx < 0 ? matchedIds.length - 1 : (currentMatchIdx - 1 + matchedIds.length) % matchedIds.length;
    setCurrentMatchIdx(next);
    scrollToMessage(matchedIds[next]);
  };

  const goToNext = () => {
    if (matchedIds.length === 0) return;
    const next = currentMatchIdx < 0 ? 0 : (currentMatchIdx + 1) % matchedIds.length;
    setCurrentMatchIdx(next);
    scrollToMessage(matchedIds[next]);
  };

  // 검색어 매칭 계산 + 오늘 가장 가까운 메시지(마지막 매칭)로 자동 이동
  useEffect(() => {
    if (!searchQuery || !searchQuery.trim()) {
      setMatchedIds([]);
      setCurrentMatchIdx(-1);
      prevSearchQueryRef.current = '';
      return;
    }
    const q = searchQuery.toLowerCase();
    // 최신순(오늘에 가까운 순)으로 reverse → 1번째 = 가장 최근
    const ids = messages
      .filter(msg => msg.content && msg.type !== 'system' && msg.type !== 'ai-suggestion' && msg.type !== 'ai-memo' && msg.content.toLowerCase().includes(q))
      .map(msg => msg._id)
      .reverse();
    setMatchedIds(ids);

    // 쿼리가 바뀌면 위치 초기화만 (Enter 누르기 전까진 스크롤 이동 없음)
    if (searchQuery !== prevSearchQueryRef.current) {
      prevSearchQueryRef.current = searchQuery;
      setCurrentMatchIdx(-1);
    }
  }, [searchQuery, messages]);

  // Enter키로 다음 결과 이동 (RoomHeader 검색창에서 dispatch)
  useEffect(() => {
    const handler = () => {
      if (matchedIds.length === 0) return;
      // 첫 Enter면 0번(가장 최근)으로, 이후엔 순차 이동
      const next = currentMatchIdx < 0 ? 0 : (currentMatchIdx + 1) % matchedIds.length;
      setCurrentMatchIdx(next);
      scrollToMessage(matchedIds[next]);
    };
    window.addEventListener('chatSearchNavigate', handler);
    return () => window.removeEventListener('chatSearchNavigate', handler);
  }, [matchedIds, currentMatchIdx]);

  // 검색어 하이라이트
  const highlightText = (content) => {
    if (!searchQuery || !searchQuery.trim()) return renderMessageContent(content);
    const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = content.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchQuery.toLowerCase()
        ? <mark key={i} className="bg-blue-300 text-blue-900 rounded px-0.5">{part}</mark>
        : part
    );
  };

  // 메시지 텍스트에서 URL을 클릭 가능한 링크로 변환
  const renderMessageContent = (content) => {
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const result = [];
    let lastIndex = 0;
    let match;
    while ((match = urlRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        result.push(<span key={`t${match.index}`}>{content.slice(lastIndex, match.index)}</span>);
      }
      const href = match[0].startsWith('http') ? match[0] : `https://${match[0]}`;
      result.push(
        <a
          key={`u${match.index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline text-blue-600 break-all"
          onClick={(e) => e.stopPropagation()}
        >
          {match[0]}
        </a>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < content.length) {
      result.push(<span key={`t${lastIndex}`}>{content.slice(lastIndex)}</span>);
    }
    return result.length > 0 ? result : content;
  };

  // 메시지별 안 읽은 인원 수 계산 (카카오톡 읽음 확인)
  // 나 자신은 채팅방을 열고 있으므로 항상 "읽음" → totalCount에서 1 제외
  const getUnreadCount = (msgCreatedAt, myId) => {
    const { readMap, totalCount } = readStatus;
    if (!totalCount) return 0;
    const myIdStr = myId?.toString();
    const effectiveTotal = totalCount - 1; // 나 제외한 다른 인원 수
    if (effectiveTotal <= 0) return 0;
    const msgTime = new Date(msgCreatedAt).getTime();
    let readCount = 0;
    Object.entries(readMap).forEach(([userId, readAt]) => {
      if (myIdStr && userId === myIdStr) return; // 나 자신 제외
      if (readAt && new Date(readAt).getTime() >= msgTime) readCount++;
    });
    return Math.max(0, effectiveTotal - readCount);
  };

  // 다른 사람이 보낸 첫 미읽음 메시지 인덱스
  // - 본인 메시지 제외: 내가 보낸 메시지 앞에는 구분선 표시 안 함
  // - system/ai-suggestion 제외
  // - myEntryReadAt이 없으면(null) -1 → 구분선 표시 안 함
  const firstUnreadOtherIdx = useMemo(() => {
    if (!myEntryReadAt) return -1;
    const myId = (user?._id || user?.id || '').toString();
    return messages.findIndex(m => {
      const sid = (m.sender?._id || m.sender?.id ||
        (typeof m.sender === 'string' ? m.sender : '')).toString();
      return sid !== myId &&
        !['system', 'ai-suggestion', 'ai-memo'].includes(m.type) &&
        new Date(m.createdAt) > new Date(myEntryReadAt);
    });
  }, [messages, myEntryReadAt, user]);

  // 이미지 뷰어용 이미지 메시지 목록
  const viewerImages = useMemo(() => {
    const myId = (user?._id || user?.id || '').toString();
    return messages
      .filter(m => m.type === 'file' && m.fileType?.startsWith('image/'))
      .map(m => ({
        src: m.fileUrl || (m.fileData ? `data:${m.fileType};base64,${m.fileData}` : ''),
        senderName: m.sender?.firstName || '',
        createdAt: m.createdAt,
        messageId: m._id,
        fileName: m.fileName || '이미지',
        isMe: (m.sender?._id || m.sender?.id || (typeof m.sender === 'string' ? m.sender : '')).toString() === myId,
      }));
  }, [messages, user]);

  const openImageViewer = (msg) => {
    const idx = viewerImages.findIndex(v => v.messageId === msg._id);
    setViewerState({ initialIndex: idx >= 0 ? idx : 0 });
  };

  const openVideoViewer = (msg) => {
    setVideoViewerState({ msg });
  };

  const scrollToBottom = (force = false) => {
    if (force || !isUserScrolling) {
      // scrollIntoView 'smooth'는 메시지 로드 직후 이미지가 덜 로딩된 상태에서
      // 애니메이션이 중간에 멈추는 버그 발생 → scrollTop 직접 설정으로 즉시 이동
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  };

  // 스크롤 위치 감지
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // 하단 50px 이내

    setIsUserScrolling(!isAtBottom);

    // 상단 100px 이내 → 이전 메시지 불러오기
    if (scrollTop < 100 && !isLoadingMoreRef.current) {
      loadMoreMessages();
    }
  };

  // 메시지 삭제 핸들러
  const handleDeleteMessage = async (messageId) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/message/${messageId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        // 삭제 성공 시 로컬 상태에서 바로 제거
        setMessages(prev => prev.filter(msg => msg._id !== messageId));
      } else {
        const data = await res.json();
        showToast(data.msg || '삭제에 실패했습니다.', 'error');
      }
      setDeleteTarget(null);
    } catch (error) {
      console.error('Delete message error:', error);
      showToast('삭제에 실패했습니다.', 'error');
      setDeleteTarget(null);
    }
  };

  // 메시지 수정 핸들러
  const handleEditMessage = async () => {
    if (!editingMsg || !input.trim() || isSending) return;
    setIsSending(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/message/${editingMsg._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: input.trim() })
      });
      if (res.ok) {
        setMessages(prev => prev.map(m =>
          m._id === editingMsg._id ? { ...m, content: input.trim(), isEdited: true } : m
        ));
        setInput('');
        setEditingMsg(null);
      } else {
        showToast('수정에 실패했습니다.', 'error');
      }
    } catch {
      showToast('수정 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsSending(false);
    }
  };

  // 파일 업로드 핸들러
  // 단일 파일 실제 업로드
  const uploadSingleFile = async (file) => {
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setToast({ message: isVideo ? '동영상은 100MB 이하여야 합니다.' : '파일 크기는 10MB 이하여야 합니다.', type: 'error' });
      return;
    }
    const token = await auth.currentUser?.getIdToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('originalFileName', file.name);
    const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });
    if (!res.ok) throw new Error('파일 업로드 실패');
  };

  // 파일 선택 시 → 이미지/동영상은 미리보기, 문서는 바로 업로드
  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const mediaFiles = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));
    const docFiles = files.filter(f => !f.type.startsWith('image/') && !f.type.startsWith('video/'));

    // 문서는 바로 업로드
    if (docFiles.length > 0) {
      setIsUploading(true);
      try {
        for (const file of docFiles) await uploadSingleFile(file);
      } catch {
        setToast({ message: '파일 업로드에 실패했습니다.', type: 'error' });
      } finally {
        setIsUploading(false);
      }
    }

    // 이미지/동영상은 미리보기 큐에 추가
    if (mediaFiles.length > 0) {
      const previews = mediaFiles.map(file => ({
        file,
        url: URL.createObjectURL(file),
        isVideo: file.type.startsWith('video/'),
      }));
      setPendingFiles(prev => [...prev, ...previews]);
    }
  };

  // 미리보기 확인 후 실제 업로드
  const handleConfirmUpload = async () => {
    if (!pendingFiles.length) return;
    const toUpload = [...pendingFiles];
    setPendingFiles([]);
    toUpload.forEach(f => URL.revokeObjectURL(f.url));
    setIsUploading(true);
    try {
      for (const { file } of toUpload) await uploadSingleFile(file);
    } catch {
      setToast({ message: '파일 업로드에 실패했습니다.', type: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  // 파일 다운로드 핸들러
  const handleFileDownload = async (fileUrl, fileName) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(fileUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch file');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      setToast({ message: '다운로드에 실패했습니다.', type: 'error' });
    }
  };

  // 메시지/readStatus 업데이트 시 스크롤 처리
  useEffect(() => {
    if (justLoadedMoreRef.current) {
      // 이전 메시지 더 불러오기: 스크롤 위치 유지
      justLoadedMoreRef.current = false;
      const container = messagesContainerRef.current;
      if (container && prevScrollHeightRef.current > 0) {
        container.scrollTop = container.scrollHeight - prevScrollHeightRef.current;
        prevScrollHeightRef.current = 0;
      }
      return;
    }

    if (!hasScrolledInitiallyRef.current) {
      if (messages.length === 0) return; // 메시지 없으면 대기
      if (myEntryReadAt === undefined) {
        // fetchReadStatus 미완료 → 캐시 메시지만 있는 상태
        // 맨 아래로 임시 스크롤 (fetchReadStatus 완료 시 unread 위치로 재스크롤)
        const el = messagesContainerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
        return;
      }
      // messages + myEntryReadAt 모두 준비 → 정식 초기 스크롤
      hasScrolledInitiallyRef.current = true;
      requestAnimationFrame(() => {
        if (unreadDividerRef.current) {
          // 구분선이 있으면 구분선 위치로 스크롤 (첫 안읽은 메시지가 화면 상단에 오도록)
          unreadDividerRef.current.scrollIntoView({ block: 'start' });
        } else {
          scrollToBottom();
        }
      });
      return;
    }

    // 이후 새 메시지(소켓 수신 등): 맨 아래로 스크롤
    scrollToBottom();
  }, [messages, myEntryReadAt]);

  // 타이핑 인디케이터 등장 시 자동 스크롤 (인디케이터가 마지막 메시지를 가리지 않도록)
  useEffect(() => {
    if (typingUsers.length > 0) scrollToBottom();
  }, [typingUsers.length]);

  // 모바일 키보드 등장 시 스크롤 하단 고정 (iOS Safari visualViewport)
  useEffect(() => {
    const vp = window.visualViewport;
    if (!vp) return;
    const handler = () => scrollToBottom();
    vp.addEventListener('resize', handler);
    return () => vp.removeEventListener('resize', handler);
  }, []);

  // 토스트 자동 닫기
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // 토스트 표시 헬퍼 함수
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // 입력창 onChange: 타이핑 이벤트 emit
  const handleInputChange = (e) => {
    setInput(e.target.value);
    // 완전히 지울 때만 AI 변환 상태 초기화 (편집 시에는 유지)
    if (!e.target.value.trim()) {
      if (aiInputTransformed) setAiInputTransformed(false);
      if (aiSuggestions.length > 0) setAiSuggestions([]);
    }
    const myId = user?._id || user?.id;
    if (!e.target.value.trim()) {
      if (typingSentRef.current) {
        socketRef.current?.emit('stop-typing', { roomId, userId: myId });
        typingSentRef.current = false;
      }
      if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
      return;
    }
    if (!typingSentRef.current) {
      socketRef.current?.emit('typing', { roomId, userId: myId, userName: user?.firstName || '...' });
      typingSentRef.current = true;
    }
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    stopTypingTimerRef.current = setTimeout(() => {
      socketRef.current?.emit('stop-typing', { roomId, userId: myId });
      typingSentRef.current = false;
    }, 2000);
  };

  // 2. 메시지 전송
  const handleSend = async (e) => {
    e.preventDefault();
    // 수정 모드
    if (editingMsg) { await handleEditMessage(); return; }
    // 텍스트 없이 파일만 있는 경우 → 파일 전송
    if (!input.trim()) {
      if (pendingFiles.length > 0) await handleConfirmUpload();
      return;
    }
    if (isSending) return;

    // 전송 시 타이핑 상태 즉시 해제
    const myId = user?._id || user?.id;
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    socketRef.current?.emit('stop-typing', { roomId, userId: myId });
    typingSentRef.current = false;

    const content = input;
    setInput(''); // UI 즉시 반응
    setAiInputTransformed(false);
    setAiSuggestions([]);
    setIsSending(true);

    try {
      await auth.authStateReady?.();
      const token = await auth.currentUser?.getIdToken();

      // 마법봉을 거쳐서 온 텍스트 그대로 전송 (자동교정 없음)
      const finalContent = content;

      const res = await fetch(`${API_BASE_URL}/api/chat/${roomId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: finalContent, type: 'text' })
      });

      if (!res.ok) {
        // 전송 실패 시 입력 복구
        setInput(content);
        setToast({ message: '메시지 전송에 실패했습니다. 다시 시도해 주세요.', type: 'error' });
      } else {
        if (onMessageSent) onMessageSent(finalContent);
        // ARES 엔진에 메시지 분석 요청 (fire-and-forget)
        engineService.sendAction(roomId, finalContent)
          .then(r => console.log('[ARES] action:', r?.action?.type || 'no action', r))
          .catch(e => console.error('[ARES] sendAction failed:', e.message, e));
        // 텍스트 전송 성공 후 파일도 있으면 함께 업로드
        if (pendingFiles.length > 0) handleConfirmUpload();
      }
    } catch (error) {
      console.error('Send error:', error);
      // 네트워크 오류 시 입력 복구
      setInput(content);
      setToast({ message: '전송 중 오류가 발생했습니다. 네트워크를 확인해 주세요.', type: 'error' });
    } finally {
      setIsSending(false);
    }
  };

  // 3. AI 버튼 클릭: 입력창 텍스트 실시간 변환
  const handleAiButton = async () => {
    if (!input.trim() || aiTransformingRef.current) return;
    aiTransformingRef.current = true;
    setIsAiTransforming(true);
    setAiSuggestions([]);
    try {
      await auth.authStateReady?.();
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('auth_token_missing');
      // 모든 활성 모드를 transform-input에 전달 (king 포함 — 입력창에서 왕 말투 미리 보여줌)
      const activeModes = ['spellcheck', 'spacing', 'king', 'smart'].filter(m => aiModes?.[m]);
      if (activeModes.length === 0) return;
      const memberNameList = roomMembers
        .map(m => { const u = m.user || m; return [u.firstName, u.lastName, u.nickname, m.nickname].filter(Boolean).join(''); })
        .filter(n => n.length > 0);
      const res = await fetch(`${API_BASE_URL}/api/chat/transform-input`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: input, modes: activeModes, memberNames: memberNameList })
      });
      if (res.status === 429) throw new Error('rate_limited');
      if (!res.ok) throw new Error(`server_error_${res.status}`);
      const data = await res.json();
      if (data.isAmbiguous && data.alternatives?.length >= 2) {
        setAiSuggestions(data.alternatives);
      } else {
        const transformed = data.transformed || input;
        setInput(transformed);
        setAiInputTransformed(true);
      }
    } catch (e) {
      console.error('[AI버튼] 오류:', e?.message);
      const msg = e?.message === 'auth_token_missing'
        ? '로그인 세션이 만료됐습니다. 앱을 다시 시작해주세요.'
        : e?.message === 'rate_limited'
          ? 'AI 요청이 너무 많습니다. 잠시 후 다시 시도해주세요.'
          : e?.message?.startsWith('server_error_401')
            ? '인증 오류. 앱을 다시 시작해주세요.'
            : 'AI 변환 실패. 다시 시도해주세요.';
      setToast({ message: msg, type: 'error' });
    } finally {
      aiTransformingRef.current = false;
      setIsAiTransforming(false);
    }
  };

  // 4. AI 제안 메시지 클릭 시 일정관리 모달 열기
  const handleOpenSuggestionModal = () => {
    setShowSuggestionModal(true);
  };

  // 가장 최근 수정 시스템 메시지 인덱스 (최신=파란, 이전=회색)
  const lastSystemChangeIdx = messages.reduce(
    (last, m, i) => (m.type === 'system' && (m.content || '').includes(' → ') ? i : last), -1
  );

  // "삭제됐습니다" system 메시지에서 suggestionId 추출 → 해당 제안 비활성화
  const cancelledSuggestionIds = new Set(
    messages
      .filter(m => m.type === 'system' && m.content?.includes('삭제') && m.suggestionId)
      .map(m => String(m.suggestionId))
  );

  // "→" 포함 수정 system 메시지에서 suggestionId 추출 → 해당 ai-suggestion을 "(수정됨)" 회색 처리
  // ARES PATCH 수정 시 새 ai-suggestion 없이 system 메시지만 생기므로, 기존 pill을 수정됨 표시
  const modifiedSuggestionIds = new Set(
    messages
      .filter(m => m.type === 'system' && (m.content || '').includes(' → ') && m.suggestionId)
      .map(m => String(m.suggestionId))
  );

  // 같은 날짜 일정 변경 시 대체된 구 제안 ID → 회색 처리
  // ai-suggestion 메시지의 prevSuggestionId 필드에서 추출
  const replacedSuggestionIds = new Set(
    messages
      .filter(m => m.type === 'ai-suggestion' && m.prevSuggestionId)
      .map(m => String(m.prevSuggestionId))
  );

  return (
    <div className="flex flex-col h-full w-full bg-gray-100 relative overflow-x-hidden">

      {/* 검색 결과 네비게이션 바 - Enter 누른 후에만 표시 */}
      {currentMatchIdx >= 0 && searchQuery && (
        <div className={`flex items-center justify-between text-xs px-4 py-1 shrink-0 mt-14 ${matchedIds.length > 0 ? 'bg-blue-500 text-white' : 'bg-gray-400 text-white'}`}>
          {matchedIds.length > 0 ? (
            <>
              <button onClick={goToPrev} className="p-1 hover:bg-blue-400 rounded" title="이전">
                <ChevronUp size={14} />
              </button>
              <span className="font-medium">
                {currentMatchIdx + 1} / {matchedIds.length}건
              </span>
              <button onClick={goToNext} className="p-1 hover:bg-blue-400 rounded" title="다음">
                <ChevronDown size={14} />
              </button>
            </>
          ) : (
            <span className="w-full text-center">검색 결과 없음</span>
          )}
        </div>
      )}

      {/* 연쇄재배치 배너 */}
      {cascadeProposal && (
        <div className="px-4 pt-2 pb-1">
          <CascadeProposedBanner
            proposal={cascadeProposal}
            onDismiss={() => setCascadeProposal(null)}
            onOpen={() => {
              const proposal = cascadeProposal;
              setCascadeProposal(null);
              onAresSuggestionClick?.(null, proposal);
            }}
          />
        </div>
      )}

      {/* 메시지 리스트 */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4"
      >
        {/* 이전 메시지 로딩 인디케이터 */}
        {isLoadingMore && (
          <div className="flex justify-center py-3">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent" />
          </div>
        )}
        {!hasMore && messages.length > 0 && (
          <div className="flex justify-center py-2">
            <span className="text-xs text-gray-400">첫 번째 메시지입니다</span>
          </div>
        )}

        {messages.map((msg, idx) => {
          // 보낸 사람 식별
          const senderObj = msg.sender;
          const senderEmail = senderObj && typeof senderObj === 'object' ? senderObj.email : null;
          const senderId = senderObj && typeof senderObj === 'object' ? (senderObj._id || senderObj.id) : senderObj;
          const myEmail = user?.email;
          const myId = user?._id || user?.id;
          let isMe = false;
          if (senderEmail && myEmail) {
            isMe = senderEmail === myEmail;
          } else {
            isMe = senderId && myId && senderId.toString() === myId.toString();
          }

          // 날짜 구분선 체크
          const currentMsgDate = new Date(msg.createdAt).toLocaleDateString('ko-KR');
          const prevMsg = idx > 0 ? messages[idx - 1] : null;
          const prevMsgDate = prevMsg ? new Date(prevMsg.createdAt).toLocaleDateString('ko-KR') : null;
          const showDateDivider = !prevMsg || currentMsgDate !== prevMsgDate;

          // 날짜 포맷
          const getDateLabel = (dateStr) => {
            const msgDate = new Date(dateStr);
            const today = new Date();
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            const msgDateStr = msgDate.toLocaleDateString('ko-KR');
            const todayStr = today.toLocaleDateString('ko-KR');
            const yesterdayStr = yesterday.toLocaleDateString('ko-KR');
            if (msgDateStr === todayStr) return '오늘';
            if (msgDateStr === yesterdayStr) return '어제';
            const year = msgDate.getFullYear();
            const month = msgDate.getMonth() + 1;
            const day = msgDate.getDate();
            const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
            const weekday = weekdays[msgDate.getDay()];
            return `${year}년 ${month}월 ${day}일 ${weekday}요일`;
          };

          const isSystem = msg.type === 'system';
          const isFile = msg.type === 'file';
          // 같은 날짜 일정으로 대체된 구 제안 → "(변경됨)" 회색 처리
          const isOutdatedSuggestion = msg.type === 'ai-suggestion' && replacedSuggestionIds.has(String(msg.suggestionId));
          // 삭제된 제안은 비활성화
          const isCancelledSuggestion = msg.type === 'ai-suggestion' && cancelledSuggestionIds.has(String(msg.suggestionId));
          // ARES PATCH 수정된 제안 → "(수정됨)" 회색 처리
          const isModifiedSuggestion = msg.type === 'ai-suggestion' && modifiedSuggestionIds.has(String(msg.suggestionId));
          const isDisabledSuggestion = isOutdatedSuggestion || isCancelledSuggestion || isModifiedSuggestion;
          const isImage = msg.fileType?.startsWith('image/');
          const isVideo = msg.fileType?.startsWith('video/');
          const fileName = msg.fileName || '파일';

          // 파일 URL 구성 (디버깅 추가)
          let fileUrl = msg.fileUrl;
          if (fileUrl && !fileUrl.startsWith('http')) {
            fileUrl = `${API_BASE_URL}${fileUrl}`;
          }

          // 여기까지 읽었습니다 구분선: firstUnreadOtherIdx와 일치하는 메시지 앞에만 표시
          // 본인 메시지·system·ai-suggestion 제외 → 내가 보낸 메시지 앞에는 구분선 안 뜸
          const showUnreadDivider = firstUnreadOtherIdx !== -1 && idx === firstUnreadOtherIdx;

return (
            <React.Fragment key={idx}>
              {/* 날짜 구분선 */}
              {showDateDivider && (
                <div className="flex items-center justify-center my-6">
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="px-4 text-xs text-gray-500 font-medium bg-gray-100 rounded-full py-1">
                    {getDateLabel(msg.createdAt)}
                  </span>
                  <div className="flex-1 h-px bg-gray-300"></div>
                </div>
              )}

              {/* 여기까지 읽었습니다 구분선 */}
              {showUnreadDivider && (
                <div ref={unreadDividerRef} className="flex items-center justify-center my-2">
                  <div className="flex-1 h-px bg-blue-300"></div>
                  <span className="px-3 py-1 text-xs text-blue-500 font-semibold bg-blue-50 rounded-full whitespace-nowrap mx-2">
                    여기까지 읽었습니다
                  </span>
                  <div className="flex-1 h-px bg-blue-300"></div>
                </div>
              )}

              {/* 시스템 메시지 */}
              {isSystem && (() => {
                const isClickable = msg.suggestionId && !msg.content?.includes('삭제');
                const hasChange = (msg.content || '').includes(' → ');
                const isLatestChange = hasChange && idx === lastSystemChangeIdx;
                // suggestionId 포함된 " → " 메시지 = 재배치/수정 알림 → 항상 회색 (파란색은 ai-suggestion 카드가 담당)
                const isModificationNotice = hasChange && !!msg.suggestionId;

                // 변경사항 있는 메시지: 최신이고 수정알림 아닌 경우만 파란 pill, 나머지 회색
                const pillClass = (hasChange && isLatestChange && !isModificationNotice)
                  ? `bg-blue-100 text-blue-700 text-xs py-1 px-3 rounded-full font-medium${isClickable ? ' cursor-pointer hover:bg-blue-200 transition-colors' : ''}`
                  : `bg-gray-200 text-gray-500 text-xs py-1 px-3 rounded-full${isClickable ? ' cursor-pointer hover:bg-gray-300 transition-colors' : ''}`;

                // 변경사항 없는 메시지는 content 그대로 표시
                if (!hasChange) {
                  return (
                    <div className="flex justify-center my-2">
                      <span
                        onClick={isClickable ? handleOpenSuggestionModal : undefined}
                        className={pillClass}
                      >
                        {msg.content}
                      </span>
                    </div>
                  );
                }

                // 변경사항 포함: "라벨: 이전값 → 새값" 파싱해서 취소선/볼드 인라인 표시
                const lines = (msg.content || '').split('\n');
                const nodes = [];
                lines.forEach((line, li) => {
                  if (li > 0) nodes.push(<br key={`br-${li}`} />);
                  if (line.includes(' → ')) {
                    // 여러 변경사항은 " / " 로 구분
                    const chunks = line.split(' / ');
                    chunks.forEach((chunk, ci) => {
                      if (ci > 0) nodes.push(<span key={`sep-${li}-${ci}`}> · </span>);
                      if (chunk.includes(' → ')) {
                        const arrowIdx = chunk.indexOf(' → ');
                        const before = chunk.substring(0, arrowIdx);
                        const after = chunk.substring(arrowIdx + 3);
                        const colonIdx = before.indexOf(': ');
                        const label = colonIdx >= 0 ? before.substring(0, colonIdx + 2) : '';
                        const oldVal = colonIdx >= 0 ? before.substring(colonIdx + 2) : before;
                        nodes.push(
                          <span key={`c-${li}-${ci}`}>
                            {label}
                            <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{oldVal}</span>
                            {' → '}
                            <strong>{after}</strong>
                          </span>
                        );
                      } else {
                        nodes.push(<span key={`p-${li}-${ci}`}>{chunk}</span>);
                      }
                    });
                  } else {
                    nodes.push(<span key={`l-${li}`}>{line}</span>);
                  }
                });

                return (
                  <div className="flex justify-center my-2">
                    <span
                      onClick={isClickable
                        ? () => onAresSuggestionClick
                          ? onAresSuggestionClick(msg.suggestionId)
                          : handleOpenSuggestionModal()
                        : undefined}
                      className={pillClass}
                      style={{ display: 'inline-block', maxWidth: '88%', textAlign: 'center', wordBreak: 'break-word' }}
                    >
                      {nodes}
                    </span>
                  </div>
                );
              })()}

              {/* AI 제안 메시지 (클릭 가능) */}
              {msg.type === 'ai-suggestion' && (
                <div className="flex justify-center my-2">
                  <span
                    onClick={() => {
                      if (isCancelledSuggestion) {
                        setToast({ message: '삭제된 일정입니다', type: 'info' });
                        return;
                      }
                      if (isModifiedSuggestion) {
                        setToast({ message: '수정된 일정입니다', type: 'info' });
                        return;
                      }
                      if (isOutdatedSuggestion) {
                        setToast({ message: '변경된 일정입니다', type: 'info' });
                        return;
                      }
                      onAresSuggestionClick ? onAresSuggestionClick(msg.suggestionId) : handleOpenSuggestionModal();
                    }}
                    className={`text-xs py-1 px-3 rounded-full transition-colors font-medium
                      ${isDisabledSuggestion
                        ? 'bg-gray-200 text-gray-400 cursor-default'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer'
                      }`}
                  >
                    📅 {msg.content}{isCancelledSuggestion ? ' (삭제됨)' : isModifiedSuggestion ? ' (수정됨)' : isOutdatedSuggestion ? ' (변경됨)' : ''}
                  </span>
                </div>
              )}

              {/* AI 메모 메시지 */}
              {msg.type === 'ai-memo' && (
                <div className="flex justify-center my-2">
                  <span className="text-xs py-1 px-3 rounded-full font-medium bg-amber-100 text-amber-800 border border-amber-200">
                    📝 {msg.content}
                  </span>
                </div>
              )}

              {/* 파일 메시지 */}
              {isFile && !isSystem && (
                  <div className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'} mb-2`}>
                {!isMe && (
                  <div className="flex flex-col items-center mr-2 self-start">
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white text-xs font-bold overflow-hidden">
                      {msg.sender?.profileImage ? (
                        <img src={msg.sender.profileImage} alt="profile" className="w-full h-full object-cover" />
                      ) : (
                        getDisplayName(msg.sender?._id || msg.sender?.id, msg.sender?.firstName)?.[0] || '?'
                      )}
                    </div>
                  </div>
                )}
                <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} max-w-[75%]`}>
                  {!isMe && <span className="text-xs text-gray-500 mb-1 ml-1">{getDisplayName(msg.sender?._id || msg.sender?.id, msg.sender?.firstName)}</span>}
                  {isVideo ? (
                    <div className={`flex flex-row gap-1 items-end ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                      {/* 동영상: 1탭 → 인라인 재생, 전체화면 버튼 → VideoViewer */}
                      <div
                        className="relative rounded-lg shadow-md overflow-hidden"
                        style={{ width: '240px', height: '160px', flexShrink: 0, background: '#1a1a1a' }}
                        onContextMenu={e => { e.preventDefault(); const rect = e.currentTarget.getBoundingClientRect(); setMsgContextMenu({ msg, rect }); }}
                      >
                        {inlinePlayingId === msg._id ? (
                          /* ── 인라인 재생 중 ── */
                          <>
                            <video
                              src={fileUrl}
                              autoPlay
                              playsInline
                              controls
                              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                            />
                            {/* 전체화면 버튼 */}
                            <button
                              onClick={(e) => { e.stopPropagation(); openVideoViewer(msg); }}
                              style={{ position: 'absolute', top: 8, right: 8, width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                            >
                              <Maximize2 size={14} color="#fff" />
                            </button>
                          </>
                        ) : (
                          /* ── 썸네일 상태 ── */
                          <div
                            className="w-full h-full cursor-pointer"
                            onClick={() => setInlinePlayingId(msg._id)}
                            onTouchStart={e => { const el = e.currentTarget; longPressTimer.current = setTimeout(() => { const rect = el.getBoundingClientRect(); setMsgContextMenu({ msg, rect }); }, 500); }}
                            onTouchEnd={() => clearTimeout(longPressTimer.current)}
                            onTouchMove={() => clearTimeout(longPressTimer.current)}
                          >
                            <video
                              src={`${fileUrl}#t=0.001`}
                              preload="metadata"
                              playsInline
                              muted
                              style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none', display: 'block' }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <div className="w-12 h-12 rounded-full bg-white/80 flex items-center justify-center">
                                <Play size={22} className="text-gray-800 ml-1" fill="currentColor" />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-0.5 items-center mb-1">
                        {msg.isEdited && <span className="text-[10px] text-gray-400 leading-none">수정됨</span>}
                        <span className="text-[10px] text-gray-400 leading-none">
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ) : isImage ? (
                    <div className={`flex flex-row gap-1 items-end ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                        <img
                          src={fileUrl}
                          alt={fileName}
                          className={`object-cover rounded-lg shadow-md cursor-pointer ${isMe ? 'hover:opacity-80' : ''}`}
                          style={{ width: '150px', height: '150px', minWidth: '150px', minHeight: '150px', maxWidth: '150px', maxHeight: '150px' }}
                          onClick={() => openImageViewer(msg)}
                          onError={(e) => { e.target.style.display = 'none'; }}
                          onContextMenu={e => { e.preventDefault(); const rect = e.currentTarget.getBoundingClientRect(); setMsgContextMenu({ msg, rect }); }}
                          onTouchStart={e => { const el = e.currentTarget; longPressTimer.current = setTimeout(() => { const rect = el.getBoundingClientRect(); setMsgContextMenu({ msg, rect }); }, 500); }}
                          onTouchEnd={() => clearTimeout(longPressTimer.current)}
                          onTouchMove={() => clearTimeout(longPressTimer.current)}
                        />
                      <div className="flex flex-col gap-1 items-center mb-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileDownload(fileUrl, fileName);
                          }}
                          className="p-1.5 rounded-full bg-gray-200 hover:bg-gray-300 transition-all"
                          title="다운로드"
                        >
                          <Download size={14} className="text-gray-700" />
                        </button>
                        <span className="text-[10px] text-gray-400">
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => isMe && setDeleteTarget(msg)}
                      className={`rounded-xl shadow-sm relative overflow-hidden ${
                        isMe
                          ? 'bg-yellow-300 rounded-tr-none cursor-pointer hover:bg-yellow-400 transition-colors'
                          : 'bg-white border border-gray-200 rounded-tl-none'
                      }`}
                    >
                      {/* 문서 파일 */}
                      <div className="px-3 py-2 flex items-center gap-2 min-w-[200px]">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          {msg.fileType?.includes('pdf') ? (
                            <FileText size={24} className="text-red-500" />
                          ) : (
                            <FileText size={24} className="text-gray-500" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{fileName}</p>
                          <p className="text-xs text-gray-500">{msg.fileSize || ''}</p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFileDownload(fileUrl, fileName);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                          title="다운로드"
                        >
                          <Download size={18} className="text-gray-600" />
                        </button>
                      </div>
                    </div>
                  )}
                  {/* 시간 표시 (이미지·동영상은 각 섹션 내부에서 처리) */}
                  {!isImage && !isVideo && (
                    <span className="text-[10px] text-gray-400 mt-1 px-1">
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              </div>
              )}

              {/* 일반 텍스트 메시지 */}
              {!isSystem && !isFile && msg.type !== 'ai-suggestion' && msg.type !== 'ai-memo' && (
            <div id={`msg-${msg._id}`} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'} mb-2 ${searchQuery && msg.content?.toLowerCase().includes(searchQuery.toLowerCase()) ? 'bg-blue-50 rounded-xl -mx-1 px-1' : ''}`}>
              {!isMe && (
                <div className="flex flex-col items-center mr-2 self-start">
                  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white text-xs font-bold overflow-hidden">
                    {msg.sender?.profileImage ? (
                      <img src={msg.sender.profileImage} alt="profile" className="w-full h-full object-cover" />
                    ) : (
                      getDisplayName(msg.sender?._id || msg.sender?.id, msg.sender?.firstName)?.[0] || '?'
                    )}
                  </div>
                </div>
              )}
              <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} max-w-[75%]`}>
                {!isMe && <span className="text-xs text-gray-500 mb-1 ml-1">{getDisplayName(msg.sender?._id || msg.sender?.id, msg.sender?.firstName)}</span>}
                <div className={`flex flex-row gap-1 items-end ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div
                    onContextMenu={e => {
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      setMsgContextMenu({ msg, rect });
                    }}
                    onTouchStart={e => {
                      const el = e.currentTarget;
                      longPressTimer.current = setTimeout(() => {
                        const rect = el.getBoundingClientRect();
                        setMsgContextMenu({ msg, rect });
                      }, 500);
                    }}
                    onTouchEnd={() => { clearTimeout(longPressTimer.current); }}
                    onTouchMove={() => { clearTimeout(longPressTimer.current); }}
                    className={`px-3 py-2 rounded-xl shadow-sm relative text-sm break-words ${
                      isMe
                        ? 'bg-yellow-300 text-black rounded-tr-none cursor-pointer hover:bg-yellow-400 transition-colors'
                        : 'bg-white text-black border border-gray-200 rounded-tl-none'
                    }`}
                    style={{ whiteSpace: 'pre-wrap' }}
                  >
                    {(() => {
                      const c = transformCache[msg._id];
                      // 왕모드만 로컬 표시 변환 (spellcheck/spacing/smart는 서버 저장된 텍스트 그대로)
                      if (aiModes?.king) {
                        if (isMe && c?.king) return highlightText(c.king);
                        if (!isMe && c?.subject) return highlightText(c.subject);
                      }
                      return highlightText(msg.content);
                    })()}
                  </div>
                  {/* 읽음 숫자 + 수정됨 + 시간 (카카오톡 스타일) */}
                  <div className={`flex flex-col gap-0.5 mb-0.5 shrink-0 ${isMe ? 'items-end' : 'items-start'}`}>
                    {(() => {
                      const unread = getUnreadCount(msg.createdAt, myId);
                      return unread > 0 ? (
                        <span className="text-[10px] font-bold text-yellow-500 leading-none">{unread}</span>
                      ) : null;
                    })()}
                    {msg.isEdited && <span className="text-[10px] text-gray-400 leading-none whitespace-nowrap">수정됨</span>}
                    <span className="text-[10px] text-gray-400 leading-none whitespace-nowrap">
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
              )}
            </React.Fragment>
          );
        })}
        
        {/* 타이핑 인디케이터 — 스크롤 영역 안에 위치해야 마지막 메시지가 가려지지 않음 */}
        {typingUsers.length > 0 && (
          <div className="flex items-center gap-2 py-1 px-2">
            <div className="w-7 h-7 rounded-full bg-gray-400 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
              {typingUsers[0].userName?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="bg-white rounded-2xl px-3 py-2 flex gap-1 items-center shadow-sm border border-gray-200">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            {typingUsers.length > 1 && (
              <span className="text-xs text-gray-500">{typingUsers.length}명 입력 중</span>
            )}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 하단으로 스크롤 버튼 */}
      {isUserScrolling && !hideScrollBtn && (
        <button
          onClick={() => scrollToBottom(true)}
          className="absolute right-6 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-all z-20 animate-bounce-in"
          style={{ bottom: inputAreaHeight + 8 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      )}

      {/* 토스트 알림 (반응형) */}
      {toast && (
        <div className={`fixed top-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 animate-slide-down ${
          toast.type === 'success' ? 'bg-green-500 text-white' :
          toast.type === 'error' ? 'bg-red-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          <span className="font-medium text-sm">{toast.message}</span>
        </div>
      )}

      {/* 일정관리 모달 (SuggestionModal) */}
      <SuggestionModal
        isOpen={showSuggestionModal}
        onClose={() => setShowSuggestionModal(false)}
        roomId={roomId}
        socket={socketRef.current}
        isMobile={isMobile}
      />

      {/* 🆕 충돌 확인 모달 - Portal로 body에 렌더링 */}
      {conflictModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
            padding: '16px',
            isolation: 'isolate'
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" style={{ position: 'relative', zIndex: 1000000 }}>
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl">⚠️</span>
              </div>
              <h3 className="text-lg font-bold text-gray-800">기존 일정이 있습니다</h3>
            </div>

            {/* 충돌하는 일정 목록 */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700 font-medium mb-2">충돌하는 일정:</p>
              {conflictModal.conflicts?.map((conflict, idx) => (
                <div key={idx} className="text-sm text-red-600 flex items-center gap-2 py-1">
                  <Clock size={14} />
                  <span>{conflict.title} ({conflict.time})</span>
                </div>
              ))}
            </div>

            {/* 새 일정 정보 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-700 font-medium mb-2">참석하려는 일정:</p>
              <div className="text-sm text-blue-600">
                <div className="flex items-center gap-2 py-1">
                  <Calendar size={14} />
                  <span>{conflictModal.suggestion?.date}</span>
                </div>
                <div className="flex items-center gap-2 py-1">
                  <Clock size={14} />
                  <span>{conflictModal.suggestion?.startTime} ~ {conflictModal.suggestion?.endTime}</span>
                </div>
                <div className="font-medium mt-1">{conflictModal.suggestion?.summary}</div>
              </div>
            </div>

            <p className="text-sm text-gray-600 text-center mb-4">
              기존 일정과 시간이 겹칩니다.<br />
              그래도 참석하시겠습니까?
            </p>

            {/* 버튼 */}
            <div className="flex gap-3">
              <button
                onClick={() => setConflictModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                취소
              </button>
              <button
                onClick={() => handleForceAccept(conflictModal.suggestionId)}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                참석하기
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* 메시지 삭제 확인 모달 */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-5 mx-4 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-bold text-gray-800 mb-2">메시지 삭제</h3>
            <p className="text-sm text-gray-600 mb-4">이 메시지를 삭제하시겠습니까?</p>
            <div className="bg-gray-100 rounded-lg p-3 mb-4">
              {deleteTarget.type === 'file' ? (
                <div className="flex items-center gap-2">
                  <FileText size={20} className="text-gray-500" />
                  <p className="text-sm text-gray-700 truncate">{deleteTarget.fileName || '파일'}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-700 line-clamp-3">{deleteTarget.content}</p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                취소
              </button>
              <button
                onClick={() => handleDeleteMessage(deleteTarget._id)}
                className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 메시지 컨텍스트 메뉴 - 카카오톡 스타일 (메시지 위치 기준 팝업) */}
      {msgContextMenu && createPortal(
        (() => {
          const rect = msgContextMenu.rect;
          const menuWidth = 200;
          const menuItemHeight = 48;
          const senderId = (msgContextMenu.msg.sender?._id || msgContextMenu.msg.sender?.id)?.toString();
          const myId = (user?._id || user?.id)?.toString();
          const isOwn = senderId === myId;
          const isText = msgContextMenu.msg.type === 'text';

          // 메뉴 항목 수 계산
          const itemCount =
            (msgContextMenu.msg.content ? 1 : 0) +        // 복사
            (onAddNotice && msgContextMenu.msg.content ? 1 : 0) + // 공지
            (isOwn && isText ? 1 : 0) +                   // 수정
            (isOwn ? 1 : 0);                              // 삭제
          const menuHeight = itemCount * menuItemHeight + 8;

          // 메시지 버블 중앙 y 기준으로 위/아래 결정
          const bubbleMidY = rect ? (rect.top + rect.bottom) / 2 : window.innerHeight / 2;
          const spaceBelow = window.innerHeight - bubbleMidY;
          const showAbove = spaceBelow < menuHeight + 20;

          // x 위치: 버블의 left/right 기준, 화면 밖 넘치지 않게
          let menuLeft = rect ? rect.left : 20;
          if (menuLeft + menuWidth > window.innerWidth - 8) menuLeft = window.innerWidth - menuWidth - 8;
          if (menuLeft < 8) menuLeft = 8;

          // y 위치
          const menuTop = showAbove
            ? (rect ? rect.top - menuHeight - 8 : bubbleMidY - menuHeight - 8)
            : (rect ? rect.bottom + 8 : bubbleMidY + 8);

          return (
            <div
              className="fixed inset-0 z-[70]"
              style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
              onClick={() => setMsgContextMenu(null)}
              onContextMenu={e => e.preventDefault()}
            >
              <div
                style={{
                  position: 'fixed',
                  top: Math.max(8, Math.min(menuTop, window.innerHeight - menuHeight - 8)),
                  left: menuLeft,
                  width: menuWidth,
                  background: '#2b2b2b',
                  borderRadius: 14,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                  overflow: 'hidden',
                  animation: 'fadeScaleIn 0.15s ease-out',
                }}
                onClick={e => e.stopPropagation()}
              >
                {/* 복사 */}
                {msgContextMenu.msg.content && (
                  <button
                    style={{ width: '100%', padding: '13px 18px', background: 'none', border: 'none', color: '#fff', fontSize: 15, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onTouchStart={e => e.currentTarget.style.background = '#3a3a3a'}
                    onTouchEnd={e => e.currentTarget.style.background = 'none'}
                    onMouseEnter={e => e.currentTarget.style.background = '#3a3a3a'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => {
                      navigator.clipboard?.writeText(msgContextMenu.msg.content || '').catch(() => {});
                      setMsgContextMenu(null);
                      showToast('복사되었습니다.');
                    }}
                  >
                    <span>복사</span>
                    <span style={{ fontSize: 17, opacity: 0.7 }}>⧉</span>
                  </button>
                )}

                {/* 공지로 등록 */}
                {onAddNotice && msgContextMenu.msg.content && (
                  <button
                    style={{ width: '100%', padding: '13px 18px', background: 'none', border: 'none', borderTop: '1px solid #3a3a3a', color: '#fff', fontSize: 15, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onTouchStart={e => e.currentTarget.style.background = '#3a3a3a'}
                    onTouchEnd={e => e.currentTarget.style.background = 'none'}
                    onMouseEnter={e => e.currentTarget.style.background = '#3a3a3a'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => { onAddNotice(msgContextMenu.msg.content); setMsgContextMenu(null); }}
                  >
                    <span>공지</span>
                    <span style={{ fontSize: 17, opacity: 0.7 }}>📢</span>
                  </button>
                )}

                {/* 수정 (내 텍스트만) */}
                {isOwn && isText && (
                  <button
                    style={{ width: '100%', padding: '13px 18px', background: 'none', border: 'none', borderTop: '1px solid #3a3a3a', color: '#fff', fontSize: 15, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onTouchStart={e => e.currentTarget.style.background = '#3a3a3a'}
                    onTouchEnd={e => e.currentTarget.style.background = 'none'}
                    onMouseEnter={e => e.currentTarget.style.background = '#3a3a3a'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => {
                      setEditingMsg(msgContextMenu.msg);
                      setInput(msgContextMenu.msg.content);
                      setMsgContextMenu(null);
                      setTimeout(() => textareaRef.current?.focus(), 100);
                    }}
                  >
                    <span>수정</span>
                    <span style={{ fontSize: 17, opacity: 0.7 }}>✏️</span>
                  </button>
                )}

                {/* 삭제 (내 메시지만) */}
                {isOwn && (
                  <button
                    style={{ width: '100%', padding: '13px 18px', background: 'none', border: 'none', borderTop: '1px solid #3a3a3a', color: '#ff6b6b', fontSize: 15, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    onTouchStart={e => e.currentTarget.style.background = '#3a3a3a'}
                    onTouchEnd={e => e.currentTarget.style.background = 'none'}
                    onMouseEnter={e => e.currentTarget.style.background = '#3a3a3a'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    onClick={() => { setDeleteTarget(msgContextMenu.msg); setMsgContextMenu(null); }}
                  >
                    <span>삭제</span>
                    <span style={{ fontSize: 17, opacity: 0.7 }}>🗑</span>
                  </button>
                )}
              </div>
            </div>
          );
        })(),
        document.body
      )}

      {/* 이미지 뷰어 */}
      {viewerState && viewerImages.length > 0 && (
        <ImageViewer
          images={viewerImages}
          initialIndex={viewerState.initialIndex}
          onClose={() => setViewerState(null)}
          onDownload={(src, fileName) => handleFileDownload(src, fileName)}
          onDelete={(messageId) => {
            const msg = messages.find(m => m._id === messageId);
            if (msg) {
              setViewerState(null);
              setDeleteTarget(msg);
            }
          }}
        />
      )}

      {/* 동영상 뷰어 */}
      {videoViewerState && (
        <VideoViewer
          video={{
            src: videoViewerState.msg.fileUrl?.startsWith('http')
              ? videoViewerState.msg.fileUrl
              : `${API_BASE_URL}${videoViewerState.msg.fileUrl}`,
            senderName: videoViewerState.msg.sender?.firstName || '',
            createdAt: videoViewerState.msg.createdAt,
            messageId: videoViewerState.msg._id,
            fileName: videoViewerState.msg.fileName || '동영상',
            isMe: (videoViewerState.msg.sender?._id || videoViewerState.msg.sender?.id ||
              (typeof videoViewerState.msg.sender === 'string' ? videoViewerState.msg.sender : '')
            ).toString() === (user?._id || user?.id || '').toString(),
          }}
          onClose={() => setVideoViewerState(null)}
          onDownload={(src, fileName) => handleFileDownload(src, fileName)}
          onDelete={(messageId) => {
            const msg = messages.find(m => m._id === messageId);
            if (msg) { setVideoViewerState(null); setDeleteTarget(msg); }
          }}
        />
      )}

      {/* 소켓 끊김 배너 */}
      {!isSocketConnected && (
        <div className="bg-yellow-100 border-t border-yellow-300 text-yellow-800 text-xs text-center py-1 px-3">
          연결이 끊어졌습니다. 재연결 중...
        </div>
      )}

      {/* 입력창 영역 */}
      <div ref={inputContainerRef} className="bg-white border-t border-gray-200" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
        {/* 수정 모드 표시 */}
        {editingMsg && (
          <div className="flex items-center justify-between px-4 py-2 bg-blue-50 border-b border-blue-100">
            <div className="flex items-center gap-2">
              <Pencil size={14} className="text-blue-500" />
              <span className="text-xs text-blue-600 font-medium">메시지 수정 중</span>
            </div>
            <button
              onClick={() => { setEditingMsg(null); setInput(''); }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* AI 선택지 (애매한 경우) */}
        {aiSuggestions.length > 0 && (
          <div className="px-3 pt-2 flex gap-2 flex-wrap">
            {aiSuggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setInput(s);
                  setAiSuggestions([]);
                  setAiInputTransformed(true);
                }}
                className="px-3 py-1.5 rounded-full text-sm font-medium border-2 border-purple-400 text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSend} className="p-3 flex items-center gap-2">

          {/* + 버튼 */}
          <button
            type="button"
            onClick={() => setShowAttachMenu(v => !v)}
            disabled={isUploading || isCorrecting || !!editingMsg}
            className={`p-2 rounded-full transition-colors disabled:opacity-50 ${showAttachMenu ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:text-blue-600 hover:bg-gray-100'}`}
            title="첨부"
          >
            {isUploading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"></div>
            ) : (
              <Plus size={22} />
            )}
          </button>

          <textarea
            ref={textareaRef}
            rows={1}
            className={`flex-1 min-w-0 border rounded-2xl px-4 py-2 focus:outline-none text-sm resize-none transition-colors ${editingMsg ? 'border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-blue-50' : 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'}`}
            style={{ fontSize: '16px', maxHeight: '120px', lineHeight: '1.5', overflowY: 'hidden' }}
            placeholder={isCorrecting ? "오타 교정 중..." : isAiTransforming ? "AI 변환 중..." : editingMsg ? "메시지를 수정하세요..." : "메시지를 입력하세요..."}
            value={input}
            onChange={handleInputChange}
            disabled={isCorrecting || isAiTransforming}
            onFocus={() => setShowAttachMenu(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (input.trim() && !isSending && !isCorrecting) handleSend(e);
              }
              if (e.key === 'Escape' && editingMsg) {
                setEditingMsg(null);
                setInput('');
              }
            }}
          />

          {/* AI버튼 or 전송버튼 */}
          {(aiModes?.spellcheck || aiModes?.spacing || aiModes?.king || aiModes?.smart) && !aiInputTransformed && !editingMsg && pendingFiles.length === 0 ? (
            <button
              type="button"
              onClick={handleAiButton}
              disabled={!input.trim() || isAiTransforming || isCorrecting}
              className="bg-purple-600 text-white p-2 rounded-full hover:bg-purple-700 disabled:opacity-50 transition-colors"
              title="AI 변환"
            >
              {isAiTransforming ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              ) : (
                <Wand2 size={20} />
              )}
            </button>
          ) : (
            <button
              type="submit"
              disabled={(!input.trim() && !pendingFiles.length) || isCorrecting || isSending}
              className={`text-white p-2 rounded-full disabled:opacity-50 transition-colors ${editingMsg ? 'bg-blue-500 hover:bg-blue-600 disabled:hover:bg-blue-500' : 'bg-blue-600 hover:bg-blue-700 disabled:hover:bg-blue-600'}`}
            >
              {isCorrecting || isSending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              ) : (
                <Send size={20} />
              )}
            </button>
          )}
        </form>

        {/* 미디어 미리보기 (패널 닫혔을 때만 - 카톡 스타일 입력창 아래) */}
        {pendingFiles.length > 0 && !showAttachMenu && (
          <div className="px-3 py-2 border-t border-gray-100 bg-white">
            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
              {pendingFiles.map((pf, i) => (
                <div key={i} className="relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden bg-gray-100">
                  {pf.isVideo ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-800">
                      <Play size={20} className="text-white" fill="white" />
                    </div>
                  ) : (
                    <img src={pf.url} alt="" className="w-full h-full object-cover" />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      URL.revokeObjectURL(pf.url);
                      setPendingFiles(prev => prev.filter((_, idx) => idx !== i));
                    }}
                    className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center"
                  >
                    <X size={11} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* + 버튼 패널 (카톡 스타일) */}
        {showAttachMenu && (
          <div className="bg-white border-t border-gray-200 animate-slide-up">
            {/* 헤더: 드래그 핸들 + 닫기 */}
            <div className="flex items-center justify-between px-4 pt-2 pb-1">
              <div className="flex-1 flex justify-center">
                <div className="w-9 h-1 rounded-full bg-gray-300" />
              </div>
              <button
                type="button"
                onClick={() => setShowAttachMenu(false)}
                className="text-gray-400 hover:text-gray-600 -mr-1 p-1"
              >
                <X size={16} />
              </button>
            </div>

            {/* 선택된 미디어 미리보기 (패널 안) */}
            {pendingFiles.length > 0 && (
              <div className="px-3 pt-1 pb-1">
                <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
                  {pendingFiles.map((pf, i) => (
                    <div key={i} className="relative flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden bg-gray-100">
                      {pf.isVideo ? (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                          <Play size={16} className="text-white" fill="white" />
                        </div>
                      ) : (
                        <img src={pf.url} alt="" className="w-full h-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => { URL.revokeObjectURL(pf.url); setPendingFiles(prev => prev.filter((_, idx) => idx !== i)); }}
                        className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 flex items-center justify-center"
                      >
                        <X size={10} className="text-white" />
                      </button>
                    </div>
                  ))}
                  {/* 추가 버튼 (label 직접 탭) */}
                  <label className="flex-shrink-0 w-16 h-16 rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-0.5 text-gray-400 hover:bg-gray-50 cursor-pointer">
                    <Plus size={16} />
                    <span className="text-[9px]">추가</span>
                    <input type="file" accept="image/*,video/*" multiple onChange={handleFilesSelected} className="hidden" />
                  </label>
                </div>
              </div>
            )}

            {/* 버튼 행 */}
            <div className="px-4 py-3 flex gap-3 justify-around">
              {/* 카메라: capture="environment" → iOS 모든 버전에서 시트 없이 바로 카메라 */}
              <label className="flex flex-col items-center gap-1.5 cursor-pointer select-none">
                <div className="w-14 h-14 rounded-2xl bg-gray-800 flex items-center justify-center">
                  <Camera size={22} className="text-white" />
                </div>
                <span className="text-[11px] text-gray-600 font-medium">카메라</span>
                <input type="file" accept="image/*,video/*" capture="environment" onChange={handleFilesSelected} className="hidden" />
              </label>

              {/* 갤러리: label 직접 탭 (iOS 15+: 시트 없이 사진첩 바로 열림) */}
              <label className="flex flex-col items-center gap-1.5 cursor-pointer select-none">
                <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center">
                  <Image size={22} className="text-green-600" />
                </div>
                <span className="text-[11px] text-gray-600 font-medium">갤러리</span>
                <input type="file" accept="image/*,video/*" multiple onChange={handleFilesSelected} className="hidden" />
              </label>

              {/* 파일 */}
              <label className="flex flex-col items-center gap-1.5 cursor-pointer select-none">
                <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
                  <FileText size={22} className="text-blue-600" />
                </div>
                <span className="text-[11px] text-gray-600 font-medium">파일</span>
                <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" multiple onChange={handleFilesSelected} className="hidden" />
              </label>

              {/* AI 모드 */}
              <button
                type="button"
                onClick={() => { setShowAttachMenu(false); window.dispatchEvent(new CustomEvent('openAiSettings')); }}
                className="flex flex-col items-center gap-1.5"
              >
                <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                  <Sparkles size={22} className="text-purple-600" />
                </div>
                <span className="text-[11px] text-gray-600 font-medium">AI 모드</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupChat;
